import { BODY_LOCK_FLAG, BODY_SCROLL_POS, POST_NAV_FLAG, STATE_CLOSE, STATE_CLOSE_MOBILE, STATE_OPEN_MOBILE, STORAGE_KEY_MOBILE } from "../shared/constant";
import { SidebarElementsMobile, SidebarStateMobile } from "../types/index-type";




const ensureSidebarState = (defaultState: SidebarStateMobile): SidebarStateMobile => {
  const stored = localStorage.getItem(STORAGE_KEY_MOBILE);
  if (stored === STATE_OPEN_MOBILE || stored === STATE_CLOSE_MOBILE) {
    return stored;
  }

  localStorage.setItem(STORAGE_KEY_MOBILE, defaultState);
  return defaultState;
};

const queryElements = (): SidebarElementsMobile => {
  return {
    body: document.body,
    sidebar: document.querySelector<HTMLElement>(".sidebar"),
    main: document.querySelector<HTMLElement>(".main"),
    handle: document.querySelector<HTMLElement>(".opcl-button"),
  };
};

const toggleSidebarClasses = (
  sidebar: HTMLElement,
  main: HTMLElement,
  isOpen: boolean
): void => {
  sidebar.classList.toggle("sidebar--open", isOpen);
  main.classList.toggle("main--sidebar-overlay", isOpen);
};

const updateSidebarStateText = (handle: HTMLElement, isOpen: boolean): void => {
  handle.textContent = isOpen ? "CLOSE" : "OPEN";
};

const toggleBodyInteraction = (isOpen: boolean): void => {
  if (isOpen) {
    lockBodyScroll();
  } else {
    unlockBodyScroll();
  }
};

const applyMobileState = (state: SidebarStateMobile): void => {
  const { body, sidebar, main, handle } = queryElements();

  if (!body || !sidebar || !main || !handle) {
    return;
  }

  const isOpen = state === STATE_OPEN_MOBILE;

  toggleSidebarClasses(sidebar, main, isOpen);
  updateSidebarStateText(handle, isOpen);
  toggleBodyInteraction(isOpen);

  localStorage.setItem(STORAGE_KEY_MOBILE, state);
};

const lockBodyScroll = (): void => {
  const body = document.body;
  if (!body || body.dataset[BODY_LOCK_FLAG] === "true") {
    return;
  }

  const scrollY = window.scrollY || window.pageYOffset || 0;
  body.dataset[BODY_LOCK_FLAG] = "true";
  body.dataset[BODY_SCROLL_POS] = scrollY.toString();
  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.width = "100%";
  body.style.overflow = "hidden";
  body.classList.add("sidebar-overlay-active");
};

const handleToggleClick = (): void => {
  const next = localStorage.getItem(STORAGE_KEY_MOBILE) === STATE_OPEN_MOBILE ? STATE_CLOSE_MOBILE : STATE_OPEN_MOBILE;
  applyMobileState(next);
};

const unlockBodyScroll = (): void => {
  const body = document.body;
  if (!body) {
    return;
  }

  const wasLocked = body.dataset[BODY_LOCK_FLAG] === "true";
  const stored = body.dataset[BODY_SCROLL_POS];

  body.classList.remove("sidebar-overlay-active");
  body.style.position = "";
  body.style.top = "";
  body.style.width = "";
  body.style.overflow = "";

  delete body.dataset[BODY_LOCK_FLAG];
  delete body.dataset[BODY_SCROLL_POS];

  if (wasLocked && stored) {
    const y = Number.parseInt(stored, 10);
    if (!Number.isNaN(y)) {
      window.scrollTo(0, y);
    }
  }
};

export const mobileSidebar = (): boolean => {
  const { handle } = queryElements();

  if (!handle) {
    return false;
  }

  const hasPendingPostNavigationClose = sessionStorage.getItem(POST_NAV_FLAG) === "true";
  if (hasPendingPostNavigationClose) {
    sessionStorage.removeItem(POST_NAV_FLAG);
  }

  const initialState = hasPendingPostNavigationClose
    ? STATE_CLOSE
    : ensureSidebarState(STATE_CLOSE);

  applyMobileState(initialState);

  if (handle.dataset.mobileSidebarBound === "true") {
    return true;
  }

  handle.addEventListener("click", handleToggleClick);
  handle.dataset.mobileSidebarBound = "true";
  bindSidebarNavigation();
  bindSidebarSearchForm();

  return true;
};

const schedulePostNavigationClose = (): void => {
  sessionStorage.setItem(POST_NAV_FLAG, "true");
};

const shouldSchedulePostNavigationClose = (link: HTMLAnchorElement): boolean => {
  if (link.dataset.sidebarPostNavIgnore === "true") {
    return false;
  }

  if (link.target === "_blank") {
    return false;
  }

  const href = link.getAttribute("href");

  if (!href) {
    return false;
  }

  if (href.startsWith("#")) {
    return false;
  }

  return true;
};

function bindSidebarNavigation(): void {
  const navLinks = document.querySelectorAll<HTMLAnchorElement>(".sidebar__body a[href]");

  navLinks.forEach((link) => {
    if (link.dataset.sidebarPostNavBound === "true") {
      return;
    }

    link.addEventListener("click", () => {
      if (shouldSchedulePostNavigationClose(link)) {
        schedulePostNavigationClose();
      }
    });

    link.dataset.sidebarPostNavBound = "true";
  });
}

function bindSidebarSearchForm(): void {
  const searchForm = document.querySelector<HTMLFormElement>(".side__search__form");
  if (!searchForm || searchForm.dataset.sidebarPostNavBound === "true") {
    return;
  }

  searchForm.addEventListener("submit", schedulePostNavigationClose);
  searchForm.dataset.sidebarPostNavBound = "true";
}
